/**
 * The phone signed-in home (web-account-pages D8; shared-client-screens D3 rows H1 to H12): the
 * app's own Home view (BookingHubView) inside the phone tab layout's frame (PhoneTabsLayout),
 * fed the website session's dashboard, with the website's addresses.
 *
 * Waiting on you's rows open each form's page, a held booking's Complete step or its pay page on
 * the website. Chat opens the messages; Waitlist and Invite open the app's Coming soon pages
 * (`/waitlists`, `/invite`, D10); About opens the studio's About page (`/about`). Book is the
 * frame's (the catalog tab the studio has something on); a booking opens its page.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { webEventPath, webSeriesPath } from '@telify/shared/browser';

import { useClientShellContext } from '@/components/client/ClientShell/ClientShellContext';
import { BookingHubView, type BookingHubHandlers } from '@/pages/BookingHubPage/BookingHubView';
import { resolveBookTab } from '@/utils/bookTarget';

import { useMessagesPanel } from '../messages/panelContext';
import { useWebChat } from '../messages/WebChat';

const bookingPath = (id: string) => `/account/bookings/${encodeURIComponent(id)}`;

export function PhoneSignedInHome() {
  const { dashboard, onBook } = useClientShellContext();
  const navigate = useNavigate();
  const { openMessages } = useMessagesPanel();
  const chat = useWebChat().state;

  const on = useMemo<BookingHubHandlers>(
    () => ({
      openForm: (token) => navigate(`/form-request/${encodeURIComponent(token)}`),
      openBookingStep: (id, target) => navigate(`${bookingPath(id)}/${target}`),
      openBooking: (id) => navigate(bookingPath(id)),
      book: onBook,
      chat: () => openMessages(),
      waitlist: () => navigate('/waitlists'),
      invite: () => navigate('/invite'),
      viewAllUpcoming: () => navigate('/account/bookings'),
      openEvent: (id) => {
        const event = dashboard.happeningSoon.find((item) => item.kind === 'event' && item.id === id);
        navigate(event?.kind === 'event' ? webEventPath(event.id, event.title) : '/events');
      },
      // H8, Decision A: a series opens its own page.
      openSeries: (seriesId) => {
        const series = dashboard.happeningSoon.find((item) => item.kind === 'series' && item.seriesId === seriesId);
        navigate(webSeriesPath(seriesId, series?.kind === 'series' ? series.serviceName : ''));
      },
      seeAllEvents: () => navigate('/events'),
      about: () => navigate('/about'),
    }),
    [dashboard.happeningSoon, navigate, onBook, openMessages],
  );

  return (
    <BookingHubView
      dashboard={dashboard}
      chatUnread={chat.live ? chat.unread : dashboard.chatUnreadSecure}
      bookDisabled={resolveBookTab(dashboard.availability) === null}
      on={on}
    />
  );
}
